import {windowWidth} from 'app/utils/draggable/windowWidth';
import {windowHeight} from 'app/utils/draggable/windowHeight';
import {TableModel} from 'app/store/poker/TableModel';
import {PokerController} from 'app/controllers/PokerController';
import {defaultPositionX} from 'app/utils/draggable/defaultPositionX';
import {defaultPositionY} from 'app/utils/draggable/defaultPositionY';
import {POKER_CONTAINERS_POSITION_KEY} from 'app/utils/Consts';

interface OptionsInterface {
    controller: PokerController
    height: number,
}

const CONTAINER_WIDTH = 722;
const MAX_WIDTH_OVERLAP_PERCENT = 0.5;

export const getTablePositionX = (
    id: number,
    activeTables: Map<string, TableModel>,
    controller: PokerController
): number => {
    const positions = localStorage.getItem(POKER_CONTAINERS_POSITION_KEY);

    const savedPositions = positions ? JSON.parse(positions) : null;
    const tables = Array.from(activeTables.values());
    const activeTableIds = tables.map(t => t.currentTable.id);

    const screenW = windowWidth();

    if (activeTableIds.length <= 1) {
        const savedX = savedPositions?.[`table_${id}`]?.x || 0;
        return savedX && screenW > savedX ? savedX : defaultPositionX();
    }

    updateTablesPositionX(activeTableIds, controller, screenW);

    const currentTable = controller.store.tables.find(t => t.id === id);
    const finalX = currentTable?.positionX ?? defaultPositionX();
    return screenW > finalX ? finalX : defaultPositionX();
};

const updateTablesPositionX = (
    activeTableIds: number[],
    controller: PokerController,
    screenW: number
): void => {
    let secondRowIndex = 0;

    activeTableIds.forEach((tableId, index) => {
        const table = controller.store.tables.find(t => t.id === tableId);

        let posX = 0;
        const requiredWidth = CONTAINER_WIDTH * (index + 1);
        const availableSpace = screenW - requiredWidth;

        if (index === 0) {
            posX = 1;
        } else if (availableSpace > 0) {
            posX = CONTAINER_WIDTH * index;
        } else if (availableSpace + CONTAINER_WIDTH * MAX_WIDTH_OVERLAP_PERCENT >= 0) {
            activeTableIds.slice(0, index + 1).forEach((prevId, prevIdx) => {
                if (prevIdx !== 0 && index >= prevIdx) {
                    const compressedX = CONTAINER_WIDTH * prevIdx - -availableSpace / index * prevIdx;
                    const prevTable = controller.store.tables.find(t => t.id === prevId);

                    if (prevTable) {
                        prevTable.positionX = compressedX;
                        controller.store.updatePosition(prevId, compressedX, null);
                    }
                }
            });
            return;
        } else {
            const nextRowX = CONTAINER_WIDTH * secondRowIndex + 1;
            posX = screenW > nextRowX + CONTAINER_WIDTH ? nextRowX : defaultPositionX();
            secondRowIndex += 1;
        }

        table.positionX = posX;
        controller.store.updatePosition(tableId, posX, null);
    });
};

export const getTablePositionY = (
    id: number,
    activeTables: Map<string, TableModel>,
    options: OptionsInterface
): number => {
    const positions = localStorage.getItem(POKER_CONTAINERS_POSITION_KEY);
    const {controller, height} = options;

    const savedPositions = positions ? JSON.parse(positions) : null;
    const tables = Array.from(activeTables.values());
    const activeTableIds = tables.map(t => t.currentTable.id);

    const screenH = windowHeight();

    if (activeTableIds.length <= 1) {
        const savedY = savedPositions?.[`table_${id}`]?.y || 0;
        return savedY && screenH > savedY ? savedY : defaultPositionY(height);
    }

    updateTablesPositionY(activeTableIds, height, controller);

    const currentTable = controller.store.tables.find(t => t.id === id);
    const finalY = currentTable?.positionY ?? defaultPositionY(height);
    return screenH > finalY ? finalY : defaultPositionY(height);
};

const updateTablesPositionY = (
    activeTableIds: number[],
    containerHeight: number,
    controller: PokerController
): void => {
    const screenW = windowWidth();
    const screenH = windowHeight();

    activeTableIds.forEach((tableId, index) => {
        const table = controller.store.tables.find(t => t.id === tableId);

        const requiredWidth = CONTAINER_WIDTH * (index + 1);
        const availableSpace = screenW - requiredWidth;

        const posY =
            availableSpace > 0 || availableSpace + CONTAINER_WIDTH * MAX_WIDTH_OVERLAP_PERCENT >= 0
                ? 1
                : screenH - containerHeight;

        table.positionY = posY;
        controller.store.updatePosition(tableId, null, posY);
    });
};
